import { DatePipe, NgClass } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  ElementRef,
  inject,
  Injector,
  Signal,
  signal,
  viewChild,
} from '@angular/core';
import { FormBuilder, FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import {
  MatCell,
  MatCellDef,
  MatColumnDef,
  MatHeaderCell,
  MatHeaderCellDef,
  MatHeaderRow,
  MatHeaderRowDef,
  MatRow,
  MatRowDef,
  MatTable,
} from '@angular/material/table';
import { toSignal } from '@angular/core/rxjs-interop';

import { TranslateModule, TranslateService } from '@ngx-translate/core';
import {
  ButtonModule,
  ButtonToggle,
  ButtonToggleGroupComponent,
  DIALOG_DATA,
  DialogRef,
  FileModel,
  GalleryModule,
  IconModule,
  SelectComponent,
  TextAreaComponentModule,
  UploaderModule,
} from '@hglobal/common';
import { AdminFinancialRequestDetails, FinancialRequestStatus, UserFinancialRequestDetails } from '@pt/models';
import { DictionariesService } from '@pt/services';
import { environment } from '@pt/environment';
import { SlackLinkDirective } from '@pt/directives';
import { MAX_FILE_SIZE } from '@pt/constants';
import { NgxPermissionsService } from 'ngx-permissions';

import { FinancialRequestsStore } from './financial-requests.store';
import { AcceptDeclineButtonsComponent } from '../accept-decline-buttons/accept-decline-buttons.component';
import { HistoryItemComponent } from './components/history-item/history-item.component';

export type ViewRequestDetailsDialogData = {
  requestId: number;
  injector: Injector;
  requestDetails: Signal<UserFinancialRequestDetails | AdminFinancialRequestDetails>;
  mode: 'user' | 'admin';
};

@Component({
  selector: 'pt-view-request-details',
  templateUrl: './view-request-details.component.html',
  styleUrl: './view-request-details.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ReactiveFormsModule,
    TranslateModule,
    ButtonModule,
    IconModule,
    DatePipe,
    TextAreaComponentModule,
    ButtonToggleGroupComponent,
    SlackLinkDirective,
    GalleryModule,
    UploaderModule,
    MatTable,
    MatCellDef,
    MatRowDef,
    MatColumnDef,
    MatCell,
    MatRow,
    MatHeaderCell,
    MatHeaderRow,
    MatHeaderRowDef,
    MatHeaderCellDef,
    NgClass,
    SelectComponent,
    AcceptDeclineButtonsComponent,
    HistoryItemComponent,
  ],
})
export class ViewRequestDetailsComponent implements AfterViewInit {
  private leftContainerRef = viewChild<ElementRef<HTMLElement>>('leftContainer');
  private rightContainerRef = viewChild<ElementRef<HTMLElement>>('rightContainer');

  readonly requestStatuses = FinancialRequestStatus;
  jiraStafProject = environment.jira.baseUrl + environment.jira.browseUrl;
  readonly MAX_FILE_SIZE = MAX_FILE_SIZE;
  readonly commentsColumns = ['date', 'message', 'status', 'fromUser', 'file'];
  protected store = inject(FinancialRequestsStore);
  readonly #dialogRef = inject(DialogRef);
  readonly #formBuilder = inject(FormBuilder);
  readonly #translate = inject(TranslateService);
  readonly #dictonariesService = inject(DictionariesService);
  readonly #permissionsService = inject(NgxPermissionsService);
  readonly #changeDetectorRef = inject(ChangeDetectorRef);

  readonly isFinAdmin = !!this.#permissionsService.getPermissions()['AdminFinanceRequestsAdmin'];
  baseButtonGroup = this.#dictonariesService.financialRequestStatusesButtonToogleGroup;
  readonly #data = inject<ViewRequestDetailsDialogData>(DIALOG_DATA);
  readonly request = this.#data.requestDetails;
  readonly form = this.#formBuilder.group({
    comment: this.#formBuilder.control(
      null,
      this.isCommentRequired(this.request().status) ? Validators.required : null,
    ),
    attachment: new FormControl(null),
  });

  readonly categoryForm = this.#formBuilder.group({
    category: this.#formBuilder.control(
      this.request().category
        ? this.store.categories()
          .find(c => c.name === this.request().category).id
        : null,
      Validators.required,
    ),
  });

  readonly #selectedCategoryId = toSignal(
    this.categoryForm.controls.category.valueChanges,
    { initialValue: this.categoryForm.controls.category.value },
  );

  isHeightReceived = false;

  showAcceptDeclineCategory = computed(() => {
    const categories = this.store.categories();
    const selectedId = this.#selectedCategoryId();

    const currentName = this.request().category ?? null;
    const currentId
      = currentName && Array.isArray(categories) && categories.length
        ? categories.find(c => c.name === currentName)?.id ?? null
        : null;

    return selectedId != null && currentId != null && selectedId !== currentId;
  });

  readonly assigneeForm = this.#formBuilder.group({
    assignee: this.#formBuilder.control(
      this.request().assignee
        ? this.store.assignees()
          .find(c => c.name === this.request().assignee?.fullName)?.id
        : null,
    ),
  });

  readonly #selectedAssigneeId = toSignal(
    this.assigneeForm.controls.assignee.valueChanges,
    { initialValue: this.assigneeForm.controls.assignee.value },
  );

  showAcceptDeclineAssignee = computed(() => {
    const assignees = this.store.assignees();
    const selectedId = this.#selectedAssigneeId();

    const currentName = this.request().assignee?.fullName ?? null;
    const currentId
      = currentName && Array.isArray(assignees) && assignees.length
        ? assignees.find(c => c.name === currentName)?.id ?? null
        : null;

    return selectedId !== currentId;
  });

  tableData = computed(() => {
    return this.request().comments.map(i => {
      const comment = i.data;
      comment.file = i.files[0];
      comment.fromUser = this.store.users()
        .find(user => user.id === +comment.fromUser)?.fullName ?? comment.fromUser;
      return i.data;
    })
      .slice(0, -1);
  });
  selectedStatus = signal<string>(this.request().status);
  readonly isAdminMode = this.#data.mode === 'admin';

  #adminStatuses: Record<FinancialRequestStatus, FinancialRequestStatus[]> = {
    [FinancialRequestStatus.New]: [FinancialRequestStatus.InProgress],
    [FinancialRequestStatus.InProgress]: [FinancialRequestStatus.Completed,
      FinancialRequestStatus.Rejected],
    [FinancialRequestStatus.Completed]: [FinancialRequestStatus.InProgress],
    [FinancialRequestStatus.Rejected]: [FinancialRequestStatus.InProgress],
    [FinancialRequestStatus.Canceled]: [],
  };

  get apiS3Url(): string {
    return `financial-requests/${this.request().requestIdHash}/files/`;
  }

  get canRequestBeReOpened(): boolean {
    if (!this.isAdminMode) {
      return this.request().status === this.requestStatuses.Rejected
        || this.request().status === this.requestStatuses.Canceled
        || this.request().status === this.requestStatuses.Completed;
    }

    return false;
  }

  get adminConfirmButtonDisabled(): boolean {
    return (this.isCommentRequired(this.selectedStatus()) && this.form.controls.comment.invalid)
      || (this.selectedStatus() === this.request().status
        && (!this.form.controls.comment.value && !this.form.controls.attachment.value)
      );
  }

  get assigneePlaceholder(): string {
    if (this.store.assignees().some(assignee => assignee.id === this.request().assignee?.id)) {
      return '';
    }

    return this.request().assignee?.fullName ?? '';
  }

  get galleryImages(): FileModel[] {
    const galleryImages: FileModel[] = [];
    const comments = this.request().comments;

    comments[comments.length - 1].files.forEach(fileName => {
      if (!this.isFileNotImage(fileName)) {
        galleryImages.push({ url: fileName } as FileModel);
      }
    });

    return galleryImages;
  }

  ngAfterViewInit(): void {
    this.#setHistorySectionHeight();
  }

  refreshRequest(requestId: number): void {
    this.store.getRequestDetails({ requestId });
  }

  changeStatus(status: string, requestId: number): void {
    this.store.changeRequestStatus({ status, requestId, source: 'dialog' });
  }

  updateComments(requestId: number, requestIdHash: string, status: string): void {
    const filePath = this.form.controls.attachment.value?.url.split('/')[5];
    const files = filePath ? [filePath.slice(0, filePath.indexOf('?'))] : [];

    this.store.saveRequestEncryptedData({
      requestId,
      requestIdHash,
      status,
      message: this.form.controls.comment.value,
      files,
      source: 'dialog',
      action: 'change',
    });
    this.form.controls.comment.setValue(null);
    this.form.controls.attachment.setValue(null);
  }

  onConfirm(requestId: number, requestIdHash: string, status: string): void {
    const id = requestId ?? (this.request() as UserFinancialRequestDetails).requestId;
    this.updateComments(id, requestIdHash, status);
    if (this.request().status !== status || status === FinancialRequestStatus.InProgress) {
      this.changeStatus(status, id);
    }
  }

  getButtonsBasedOnStatus(currentStatus: FinancialRequestStatus): ButtonToggle[] {
    const history = this.request().history;
    const wasInProgress = history.some(
      item => ![FinancialRequestStatus.New, FinancialRequestStatus.Canceled].includes(item.status),
    );

    const transitions = this.isAdminMode
      ? this.#adminStatuses[currentStatus] || []
      : this.#userStatuses(wasInProgress)[currentStatus] || [];

    const allowed = new Set<FinancialRequestStatus>(transitions);

    return this.baseButtonGroup.map(source => {
      const btn = { ...source };
      if (btn.value === FinancialRequestStatus.New
        && this.request().status === FinancialRequestStatus.Canceled) {
        btn.label = this.#translate.instant('dictionary.financialRequestStatuses.reopen');
      }
      btn.isChecked = source.value === currentStatus;
      btn.isDisabled = !(btn.isChecked || allowed.has(source.value));
      return btn;
    });
  }

  getCorrectTranslation(status: string): string {
    switch (status) {
      case (this.requestStatuses.InProgress):
        return 'myRequests.viewRequestDetails.formControl.inProgressComment';
      case (this.requestStatuses.Rejected):
        return 'myRequests.viewRequestDetails.formControl.rejectComment';
      default:
        return 'myRequests.viewRequestDetails.formControl.comment';
    }
  }

  updateSelectedStatus(status: string): void {
    const requestStatus = status as FinancialRequestStatus;
    const commentControl = this.form.controls.comment;

    if (this.isCommentRequired(requestStatus)) {
      commentControl.setValidators(Validators.required);
    } else {
      commentControl.removeValidators(Validators.required);
    }

    commentControl.updateValueAndValidity();
    this.selectedStatus.set(requestStatus);
  }

  getUserFullName(userId: string): string | null {
    return this.store.users().find(user => user.id === +userId)?.fullName;
  }

  isFileNotImage(fileName: string): boolean {
    return (/\.(pdf|docx|doc|xls|xlsx)/i).test(fileName);
  }

  getPdfName(url: string): string {
    const fileNameWithExtras = url.split('/')[5];

    return fileNameWithExtras.slice(0, fileNameWithExtras.indexOf('.') + 4);
  }

  close(): void {
    this.#dialogRef.close();
  }

  isCommentRequired(status: string): boolean {
    if (this.isAdminMode) {
      return status === FinancialRequestStatus.Rejected
        || (status === FinancialRequestStatus.InProgress
          && this.request().status === FinancialRequestStatus.Rejected);
    } else {
      return status === FinancialRequestStatus.Canceled
        || this.request().status === FinancialRequestStatus.Canceled;
    }
  }

  resetData(): void {
    this.updateSelectedStatus(this.request().status);
    this.form.reset({
      comment: null,
      attachment: null,
    });
  }

  #userStatuses(wasInProgress: boolean): Record<FinancialRequestStatus, FinancialRequestStatus[]> {
    return {
      [FinancialRequestStatus.New]: [FinancialRequestStatus.Canceled],
      [FinancialRequestStatus.InProgress]: [FinancialRequestStatus.Canceled],
      [FinancialRequestStatus.Canceled]: wasInProgress
        ? [FinancialRequestStatus.InProgress]
        : [FinancialRequestStatus.New],
      [FinancialRequestStatus.Completed]: [FinancialRequestStatus.InProgress],
      [FinancialRequestStatus.Rejected]: [FinancialRequestStatus.InProgress],
    };
  }

  saveNewCategory(): void {
    this.store.updateRequestAssigneeCategory(
      {
        id: (this.request() as any).id,
        payload: { categoryId: this.categoryForm.controls.category.value },
        isListUpdated: false,
      },
    );
  }

  declineCategoryChange(): void {
    const currentName = this.request().category ?? null;
    const categories = this.store.categories();

    const currentId
      = currentName && Array.isArray(categories) && categories.length
        ? categories.find(c => c.name === currentName)?.id ?? null
        : null;

    this.categoryForm.controls.category.setValue(currentId);
  }

  saveNewAssignee(): void {
    this.store.updateRequestAssigneeCategory(
      {
        id: (this.request() as any).id,
        payload: { assigneeUserId: this.assigneeForm.controls.assignee.value },
        isListUpdated: false,
      },
    );
  }

  declineAssigneeChange(): void {
    const currentName = this.request().assignee?.fullName ?? null;
    const assignees = this.store.assignees();

    const currentId
      = currentName && Array.isArray(assignees) && assignees.length
        ? assignees.find(c => c.name === currentName)?.id ?? null
        : null;

    this.assigneeForm.controls.assignee.setValue(currentId);
  }

  #setHistorySectionHeight(): void {
    const leftSideHeight = this.leftContainerRef().nativeElement.getBoundingClientRect().height;
    this.isHeightReceived = true;
    this.#changeDetectorRef.detectChanges();
    this.rightContainerRef().nativeElement.style.maxHeight = `${leftSideHeight}px`;
    this.#changeDetectorRef.detectChanges();
  }
}
